import { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import * as RUIDialog from '@radix-ui/react-dialog';

import { useDialog } from '@edf-pkg/app-main';

import ButtonBase from '$app-web/ui-kit/components/button-base';
import ButtonGroup from '$app-web/ui-kit/components/button-group';
import Icon from '$app-web/ui-kit/components/icon';

import classes from './styles.module.scss';

const Dialog = (props) => {
    const {
        id,
        children,
        fullWidth,
        fullHeight,
        fullScreen,
        title,
        type,
        maxWidth,
        confirmAction,
        cancelAction,
        actions,
        onBackActionClick,
        onOpen,
        onClose,
        disabledCloseAction,
        disabledCloseOnOutsideClick,
        disabledCloseOnEscape,
        className,
        overPopover,
        closeOnCancel,
    } = props;

    const dialog = useDialog(id, true);

    const hasFooter = confirmAction || cancelAction || actions;

    const onCloseAction = useCallback(() => {
        if (!disabledCloseAction) {
            dialog.close();
            onClose?.();
        }
    }, [dialog, disabledCloseAction, onClose]);

    const footerCTAButtons = useMemo(() => {
        const CTAButtons = [];

        if (confirmAction) {
            CTAButtons.push({
                color: type === 'info' ? 'primary' : type,
                small: true,
                ...confirmAction,
            });
        }
        if (cancelAction) {
            CTAButtons.push({
                small: true,
                ...cancelAction,
                ...(closeOnCancel
                    ? {
                          onClick: () => {
                              cancelAction.onClick();
                              onCloseAction();
                          },
                      }
                    : {}),
            });
        }

        return CTAButtons;
    }, [cancelAction, closeOnCancel, confirmAction, onCloseAction, type]);

    useEffect(() => {
        if (dialog.isOpen && onOpen) {
            onOpen();
        }
    }, [dialog.isOpen, onOpen]);

    return (
        <RUIDialog.Root open={dialog.isOpen}>
            <RUIDialog.Overlay className={clsx(className, classes.overlay, { [classes.overPopover]: overPopover })}>
                <RUIDialog.Content
                    className={clsx(classes.content, classes[maxWidth], {
                        [classes.fullWidth]: fullWidth,
                        [classes.fullHeight]: fullHeight,
                        [classes.fullScreen]: fullScreen,
                        [classes.overPopover]: overPopover,
                    })}
                    {...(!disabledCloseOnOutsideClick ? { onInteractOutside: onCloseAction } : {})}
                    {...(!disabledCloseOnEscape ? { onEscapeKeyDown: onCloseAction } : {})}
                >
                    <div className={clsx(classes.headerWrapper, 'flex item items-center nowrap')}>
                        <div className={clsx(classes.titleWrapper, 'flex item items-center nowrap')}>
                            {onBackActionClick && (
                                <div className={clsx(classes.iconWrapper, 'item')}>
                                    {/* TODO: ButtonBase */}
                                    <ButtonBase onClick={onBackActionClick}>
                                        <Icon icon={['far', 'chevron-left']} color="gray-400" />
                                    </ButtonBase>
                                </div>
                            )}
                            {type !== 'info' && (
                                <div className={clsx(classes.iconWrapper, 'item')}>
                                    <Icon
                                        icon={
                                            type === 'warning' ? ['fas', 'exclamation-triangle'] : ['fas', 'exclamation-square']
                                        }
                                        color={type === 'warning' ? 'yellow-400' : 'red-500'}
                                    />
                                </div>
                            )}
                            <h5 className={clsx(classes.titleText, 'title item')}>{title}</h5>
                        </div>
                        <div>
                            {/* TODO: We've used ButtonBase here due to styling issues with the UI kit IconButton, but this should be replaced later when those issues are fixed. (Such as supporting circular shape transparent type)  */}
                            <ButtonBase onClick={onCloseAction} disabled={disabledCloseAction}>
                                <Icon icon={['fal', 'times']} color={disabledCloseAction ? 'gray-300' : 'gray-400'} />
                            </ButtonBase>
                        </div>
                    </div>
                    <div className={clsx(classes.bodyWrapper, 'item')}>{children}</div>
                    {hasFooter && (
                        <div className={clsx(classes.footerWrapper, 'flex item nowrap')}>
                            <div className={clsx('item xs-fill')}>{actions}</div>
                            <div className={clsx('item', classes.CTAButtonsWrapper)}>
                                {(confirmAction || cancelAction) && <ButtonGroup items={footerCTAButtons} />}
                            </div>
                        </div>
                    )}
                </RUIDialog.Content>
            </RUIDialog.Overlay>
        </RUIDialog.Root>
    );
};

Dialog.defaultProps = {
    onBackActionClick: undefined,
    type: 'info',
    className: undefined,
    maxWidth: 'xl',
    confirmAction: undefined,
    cancelAction: undefined,
    actions: undefined,
    onClose: undefined,
    onOpen: undefined,
    fullWidth: false,
    fullHeight: false,
    fullScreen: false,
    disabledCloseAction: false,
    disabledCloseOnOutsideClick: false,
    disabledCloseOnEscape: false,
    overPopover: false,
    closeOnCancel: false,
};

Dialog.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    onBackActionClick: PropTypes.func,
    type: PropTypes.oneOf(['info', 'warning', 'error']),
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    confirmAction: PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        loading: PropTypes.bool,
        loadingLabel: PropTypes.string,
    }),
    cancelAction: PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        loading: PropTypes.bool,
        loadingLabel: PropTypes.string,
    }),
    actions: PropTypes.element,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    disabledCloseAction: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    fullScreen: PropTypes.bool,
    disabledCloseOnOutsideClick: PropTypes.bool,
    disabledCloseOnEscape: PropTypes.bool,
    className: PropTypes.string,
    overPopover: PropTypes.bool,
    closeOnCancel: PropTypes.bool,
};

export default Dialog;
